import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';

import type Goal from 'webapp/models-custom/goal';

interface Args {
	model: Goal | null;
}

type Action =
	| 'toggleFavorite'
	| 'editGoal'
	| 'openGoalPermissions'
	| 'openActivityLog'
	| 'deleteGoal'
	| 'viewMetricInsights';
type Option = PopupListItem<Action> & {
	deprioritize?: boolean;
};

export default class GoalInsightsToolbarIndexComponent extends Component<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked isFavoriteToggleInProgress = false;

	close = undefined;

	get model() {
		return this.args.model;
	}

	get theme() {
		return this.navigationService.theme.id;
	}

	get isFavorite() {
		return !!this.model?.favorite;
	}

	get options() {
		const options: Option[] = [],
			goalModel = this.model;

		if (goalModel) {
			if (goalModel.canEdit) {
				options.push({
					icon: 'Preferences',
					text: 'Progress settings',
					action: 'editGoal',
				});
			}

			options.push(this.favoriteOption);

			// if (this.model.isAutomaticGoal) {
			// 	options.push({
			// 		icon: 'MetricDetail',
			// 		text: 'View metric',
			// 		action: 'viewMetricInsights',
			// 	});
			// }

			// if (this.model.canEditPermissions) {
			// 	options.push({
			// 		icon: 'UserAccess',
			// 		text: 'User access',
			// 		action: 'openGoalPermissions',
			// 	});
			// }

			if (this.currentUserService.isAdmin) {
				options.push(this.mmActivityLogsService.getObjectForOptionsPopup());
			}

			if (goalModel.canDelete) {
				options.push({
					class: 'delete',
					icon: 'Trash',
					text: 'Delete',
					action: 'deleteGoal',
				});
			}
		}

		return options;
	}

	get favoriteOption() {
		return {
			class: this.isFavorite ? 'star' : '',
			icon: this.isFavorite ? 'StarFill' : 'Star',
			text: this.model?.favoriteText,
			isLoading: this.isFavoriteToggleInProgress,
			action: 'toggleFavorite',
			deprioritize: this.isFavorite,
		} as Option;
	}

	@action
	editGoal(): void {
		if (this.model) {
			this.mmGoalsService.openEditPopup(this.model, 'goal_insights', (model) => {
				this.eventManagerService.trigger(AppEvent.GOAL_INSIGHTS_GOAL_EDITED, model);
			});
		}
	}

	@action
	deleteGoal(): void {
		if (this.model) {
			this.mmGoalsService.deleteGoal(this.model, () => {
				// the index route opens the first remaining goal (or My goals when none is left)
				this.navigationService.transitionToRoute('goals.insights.index');
			});
		}
	}

	@action
	toggleFavorite(): void {
		if (this.isFavoriteToggleInProgress || !this.model) {
			return;
		}

		this.isFavoriteToggleInProgress = true;

		this.mmGoalsService
			.toggleGoalFavorite(this.model)
			.catch(() => {
				this.infolayerService.error('Unable to update favorite status');
			})
			.finally(() => {
				this.isFavoriteToggleInProgress = false;
			});
	}

	@action
	openActivityLog() {
		if (this.model) {
			this.mmActivityLogsService.createAndOpenPopup({
				model: this.model,
			});
		}
	}

	@action
	openGoalPermissions() {
		if (this.accountService.handleDemo() || !this.model) {
			return;
		}

		this.analyticsService.event(TrackingEvent.GOAL_PERMISSIONS_POPUP, {
			goal_id: this.model.id,
		});

		this.mmGoalsService.openGoalAccessTypePopup(this.model);
	}

	@action
	viewMetricInsights() {
		const metricSetup = this.model?.mainMetricSetup,
			dateRange = this.model?.dateRange;

		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup,
				dateRange: dateRange?.formula,
			},
			'home_metrics',
		);
	}

	@action
	doAction(actionName?: string): void {
		if (!actionName) {
			return;
		}

		this[actionName as Action]();
	}
	<template>
		<CommonCollapsiblePillOptionsComponent
			@itemsList={{this.options}}
			@doAction={{this.doAction}}
			@maxVisibleItems={{1}}
		/>
	</template>
}
